// Client for the shared gazar backend, pinned to Farsi.
//
// Two bases on purpose. Loaders run in Node and can use an internal URL, which
// is why API_URL is a RUNTIME env var; the browser cannot resolve that, so
// anything fetched after hydration goes to the public origin. Getting this wrong
// is invisible in dev, where both are the same host, and fatal in production.
import { visitorId } from "./visitor";

const PUBLIC_BASE = "https://api.gazar.dev";
const BASE =
  typeof process !== "undefined" && process.env?.API_URL ? process.env.API_URL : PUBLIC_BASE;

/** The locale every request in this app pins. The backend defaults to "en". */
export const LOCALE = "fa";

export type ArticleListItem = {
  id: string;
  title: string;
  slug: string;
  category: string;
  description: string | null;
  /**
   * The cover. Shaped `{ url, formats: { large|medium|small: { url } } }`, a
   * legacy Strapi payload the English site still writes, so it is typed loosely
   * and read through `coverUrl()` rather than indexed into directly.
   */
  image: unknown;
  publishedAt: string;
  viewCount: number;
  likeCount: number;
  /** Absent when talking to a backend older than 21 Aug 2026. See verifyLocale. */
  locale?: string;
};

export type Article = ArticleListItem & {
  locale?: string;
  body: string;
  content: string;
  updatedAt: string;
  related: { title: string; slug: string; category: string; description: string | null }[];
};

/**
 * The cover image URL for an article, or null when it has none.
 *
 * Written 22 Aug 2026, when `apps/fa` began rendering covers at all. Until then
 * this app ignored the field completely: no cover on the list, none on the
 * article page, and one shared English hero as the `og:image` for every route.
 *
 * Defensive on purpose. `image` is whatever the admin wrote, it is `unknown` in
 * the type, and older rows have `null`. A cover is decoration; a thrown
 * TypeError inside a loader is a 500 on the whole page, so every branch here
 * fails to null rather than throwing.
 */
export function coverUrl(image: unknown): string | null {
  if (!image || typeof image !== "object") return null;
  const img = image as { url?: unknown; formats?: unknown };
  // Prefer a sized rendition over the original: the original is the full-size
  // generation and these render at card width.
  const formats = img.formats;
  if (formats && typeof formats === "object") {
    for (const key of ["large", "medium", "small"] as const) {
      const f = (formats as Record<string, unknown>)[key];
      if (f && typeof f === "object") {
        const u = (f as { url?: unknown }).url;
        if (typeof u === "string" && u.startsWith("https://")) return u;
      }
    }
  }
  return typeof img.url === "string" && img.url.startsWith("https://") ? img.url : null;
}

export type User = { id: string; email: string; name: string | null };

class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function get<T>(path: string, init?: RequestInit): Promise<T> {
  const base = typeof window === "undefined" ? BASE : PUBLIC_BASE;
  const res = await fetch(`${base}${path}`, { ...init, credentials: "include" });
  if (!res.ok) {
    throw new ApiError(res.status, `${res.status} on ${path}`);
  }
  return (await res.json()) as T;
}

/**
 * Trust nothing about the server's locale filtering; check it here.
 *
 * `?locale=fa` is a query parameter, and a backend that predates it strips the
 * unknown key and answers with ENGLISH articles. That is not a theoretical
 * failure: it was observed on 21 Aug 2026 against production, which happily
 * served six English pieces to a page that had asked for Farsi. Nothing about
 * the response says it is wrong, so deploy ordering was the only thing standing
 * between an English article and the Farsi home page.
 *
 * Rows without a matching `locale` are dropped. Against an old backend that
 * empties the list, which is the SAFE failure: a Farsi surface with nothing on
 * it is obviously unfinished, an English article on it is a bug nobody notices.
 */
function isFarsi(a: ArticleListItem): boolean {
  return a.locale === LOCALE;
}

/** Published Farsi articles, newest first. */
export async function listArticles(limit = 50): Promise<{ items: ArticleListItem[]; total: number }> {
  const res = await get<{ items: ArticleListItem[]; total: number }>(
    `/api/content/articles?locale=${LOCALE}&limit=${limit}`,
  );
  const items = res.items.filter(isFarsi);
  return { items, total: items.length };
}

/**
 * One Farsi article.
 *
 * `category` is part of the backend's key but not of this app's URLs: a Farsi
 * reader has no use for the English taxonomy in a path. Every article published
 * here lives under one category, so the route carries only the slug and the
 * category is supplied here.
 */
export const FA_CATEGORY = "fa";

export async function getArticle(slug: string): Promise<Article> {
  const article = await get<Article>(
    `/api/content/articles/${FA_CATEGORY}/${slug}?locale=${LOCALE}`,
  );
  // Same reasoning as the list: an old backend resolves the slug against the
  // pre-locale unique key and can hand back the English article.
  if (article.locale !== LOCALE) {
    throw new ApiError(404, `article ${slug} is not ${LOCALE}`);
  }
  return article;
}

/** Who is signed in, or null. Browser-only: it needs the .gazar.dev cookie. */
export async function me(): Promise<User | null> {
  try {
    const { user } = await get<{ user: User }>("/api/me");
    return user;
  } catch {
    return null;
  }
}

/**
 * Sign in against the shared account system.
 *
 * There is no separate Farsi account. The session is the httpOnly `token`
 * cookie scoped to `.gazar.dev`, so signing in here is the same session as
 * gazar.dev and coding.gazar.dev, and this app never sees the token. That is
 * also why every request sets `credentials: "include"`: the cookie is not
 * same-origin from this app's point of view.
 */
export async function login(email: string, password: string): Promise<User> {
  const res = await fetch(`${PUBLIC_BASE}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const body = (await res.json().catch(() => ({}))) as { user?: User; message?: string };
  if (!res.ok) {
    throw new ApiError(res.status, body.message ?? "ورود ناموفق بود");
  }
  return body.user as User;
}

/**
 * Farsi newsletter signup.
 *
 * Posts to the SAME `/api/waitlist` table every other CTA on gazar.dev uses,
 * tagged `source: "fa"` so the Farsi list is readable on its own in the admin
 * Waitlist page while there is still one pool and one unsubscribe link per
 * person. Carried over unchanged from the page's previous home on the English
 * app, so the 150-address count that the 27 Nov gate reads is continuous across
 * the move and did not reset when the surface changed.
 */
export const FA_WAITLIST_SOURCE = "fa";

/**
 * The stored source for a signup that arrived by a known route.
 *
 * Untagged stays exactly `"fa"`, so every row captured before 22 Aug 2026 stays
 * comparable and the gate's count is continuous. Tagged rows are `fa-<route>`,
 * which means the whole surface is still one prefix query:
 *
 *   SELECT source, count(*) FROM "WaitlistEntry"
 *   WHERE source LIKE 'fa%' GROUP BY source;
 *
 * Count DISTINCT email, not rows: WaitlistEntry is keyed (email, source), so
 * one person who arrives twice by two routes is two rows and one subscriber.
 */
export function faSource(src?: string | null): string {
  return src ? `${FA_WAITLIST_SOURCE}-${src}` : FA_WAITLIST_SOURCE;
}

export async function joinWaitlist(email: string, src?: string | null): Promise<void> {
  const res = await fetch(`${PUBLIC_BASE}/api/waitlist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, source: faSource(src) }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
}

/**
 * Count a read of a Farsi article.
 *
 * The Farsi app shipped without this, so `Article.viewCount` sat at 0 for the
 * one published Farsi piece and 0 meant "never instrumented", not "never read".
 * The English site has called this endpoint since long before; this app simply
 * never did.
 *
 * Browser-only and deliberately fire-and-forget. The backend de-duplicates per
 * reader and answers `counted: false` on a repeat, so a failure here costs a
 * number and never a page.
 */
export async function recordView(articleId: string): Promise<void> {
  const vid = visitorId();
  if (!vid) return;
  try {
    await fetch(`${PUBLIC_BASE}/api/content/articles/${articleId}/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId: vid }),
      keepalive: true,
    });
  } catch {
    // Counting is best-effort. Never let it break the page.
  }
}

export async function logout(): Promise<void> {
  await fetch(`${PUBLIC_BASE}/api/auth/logout`, { method: "POST", credentials: "include" });
}

export { ApiError };
